import { supabase } from "@/integrations/supabase/client";

/** Detect device type from screen width */
export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

/** Touch points reported by the device; 0 when the API is unavailable. */
function maxTouchPoints(): number {
  if (typeof navigator === 'undefined') return 0;
  const n = (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints;
  return typeof n === 'number' ? n : 0;
}

/** Parse basic browser/OS from user agent */
export function parseBrowserOS(): { browser: string; os: string } {
  if (typeof navigator === 'undefined') return { browser: 'unknown', os: 'unknown' };
  const ua = navigator.userAgent;
  // Mobile builds name themselves differently: Edge is EdgA/ on Android and
  // EdgiOS/ on iOS, Chrome and Firefox on iOS are CriOS/ and FxiOS/. Each of
  // those UAs also carries Chrome/ or Safari/, so they must be tested first.
  let browser = 'Other';
  if (ua.includes('Firefox/') || ua.includes('FxiOS/')) browser = 'Firefox';
  else if (/Edg(A|iOS)?\//.test(ua)) browser = 'Edge';
  else if (ua.includes('OPR/') || ua.includes('Opera/')) browser = 'Opera';
  else if (ua.includes('Chrome/') || ua.includes('CriOS/')) browser = 'Chrome';
  else if (ua.includes('Safari/')) browser = 'Safari';

  // Order is load-bearing. Every iPhone/iPad user agent carries the literal
  // "like Mac OS X", so testing Mac OS first swallows the whole Apple mobile
  // platform: until 2026-08-19 the iOS branch sat below it and was therefore
  // unreachable, filing 320 iPhone users and 2071 sessions under macOS.
  // Android likewise carries "Linux", and ChromeOS must precede Linux.
  let os = 'Other';
  if (ua.includes('iPhone') || ua.includes('iPad') || ua.includes('iPod')) os = 'iOS';
  else if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Android')) os = 'Android';
  // iPadOS Safari defaults to a desktop-class "Macintosh" user agent that is
  // byte-identical to a Mac's, so the UA alone cannot separate them. Touch
  // points can: iPads report 5, Macs report 0.
  else if (ua.includes('Mac OS')) os = maxTouchPoints() > 1 ? 'iOS' : 'macOS';
  else if (ua.includes('CrOS')) os = 'ChromeOS';
  else if (ua.includes('Linux')) os = 'Linux';

  return { browser, os };
}

const DEVICE_ID_KEY = 'cctvp_device_id';

/** Stable per-browser device id (localStorage). Empty string if storage is unavailable (SSR/private mode). */
export function getDeviceId(): string {
  if (typeof window === 'undefined') return '';
  try {
    let id = window.localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return ''; // private mode / storage blocked — skip device registration
  }
}

/**
 * Register this device for the per-account device limit (anti credential-sharing).
 * STAGE 1 = MONITOR: this never blocks; the RPC always allows and only reports
 * would_block. Fire-and-forget; must never break auth.
 */
export const DEVICE_LIMIT_EVENT = 'cctvp:device-limit';

export async function registerDevice(userId: string): Promise<Record<string, unknown> | null> {
  try {
    const deviceId = getDeviceId();
    if (!deviceId) return null;
    const deviceType = getDeviceType();
    const { browser, os } = parseBrowserOS();
    // Use screen.* (physical resolution, stable) not innerWidth (window size, noisy).
    const sw = typeof screen !== 'undefined' ? screen.width : null;
    const sh = typeof screen !== 'undefined' ? screen.height : null;
    const fingerprint = `${deviceType}|${os}|${sw ?? '?'}x${sh ?? '?'}`;
    const label = `${browser} · ${os} · ${sw ?? '?'}×${sh ?? '?'}`;
    const { data, error } = await supabase.rpc('register_device', {
      p_device_id: deviceId,
      p_fingerprint: fingerprint,
      p_label: label,
    });
    if (error) { console.debug('register_device failed:', error); return null; }
    const r = (data as Record<string, unknown>) ?? null;
    // Broadcast the result so the DeviceLimitGate can show/hide the block modal.
    // (When enforce=false, allowed is always true → the gate never blocks.)
    if (typeof window !== 'undefined' && r) {
      window.dispatchEvent(new CustomEvent(DEVICE_LIMIT_EVENT, { detail: r }));
    }
    return r;
  } catch (err) {
    console.debug('register_device error:', err);
    return null;
  }
}

const LOGGED_SESSION_KEY = 'cctvp_logged_session';

/** The auth session id carried in an access token. It changes only on a real sign-in. */
function authSessionId(accessToken: string | undefined): string | null {
  if (!accessToken) return null;
  try {
    const payload = accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const sid = JSON.parse(atob(payload)).session_id;
    return typeof sid === 'string' ? sid : null;
  } catch {
    return null;
  }
}

/**
 * Log login session with device info (fire-and-forget).
 *
 * supabase-js re-emits SIGNED_IN when a tab regains focus, so keying on the
 * event alone wrote a row per tab switch. With the access token, a row is
 * written once per auth session per browser.
 */
export function logLoginSession(userId: string, accessToken?: string) {
  try {
    const sid = authSessionId(accessToken);
    if (sid) {
      try {
        if (window.localStorage.getItem(LOGGED_SESSION_KEY) === sid) return;
        window.localStorage.setItem(LOGGED_SESSION_KEY, sid);
      } catch { /* storage blocked: log every time rather than never */ }
    }
    const deviceType = getDeviceType();
    const { browser, os } = parseBrowserOS();
    supabase
      .from('login_sessions')
      .insert({
        user_id: userId,
        device_type: deviceType,
        browser,
        os,
        screen_width: typeof window !== 'undefined' ? window.innerWidth : null,
        screen_height: typeof window !== 'undefined' ? window.innerHeight : null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : null,
      })
      .then(() => {}, (err: unknown) => console.debug('Login session log failed:', err));
  } catch {
    // Silent fail — logging should never break auth
  }
}
